import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { startJiraOAuth, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { jiraFormSchema, type JiraForm } from "./integrationFormSchemas";

export function JiraConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const { register, handleSubmit, formState: { errors } } = useForm<JiraForm>({
    resolver: zodResolver(jiraFormSchema),
    defaultValues: {
      name: connection?.name ?? "Jira",
      projectKey: connection?.configSummary.projectKey ?? "",
      issueType: connection?.configSummary.issueType ?? "Task",
      jql: connection?.configSummary.jql ?? "statusCategory != Done ORDER BY updated DESC",
    },
  });

  const projectKeyRegistration = register("projectKey");

  const authorize = async (values: JiraForm) => {
    const { authorizationUrl } = await startJiraOAuth({ ...values, ...(connection ? { connectionId: connection.id } : {}) });
    action.redirect(authorizationUrl);
  };

  const submit = handleSubmit((values) => action.run(connection ? "save" : "authorize", async () => {
    try {
      if (!connection) return await authorize(values);
      await updateIntegrationConnection(connection.id, { name: values.name, config: { projectKey: values.projectKey, issueType: values.issueType, jql: values.jql } });
      toast.success("Jira settings updated.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect Jira.");
    }
  }));

  const reconnect = handleSubmit((values) => action.run("authorize", async () => {
    try { await authorize(values); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to authorize connection."); }
  }));

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        IO Spoke will send you to Atlassian to approve access to one Jira site. Your Atlassian password and API tokens never enter IO Spoke.
      </div>
      <FormField id="jira-name" label="Connection name" registration={register("name")} error={errors.name} />
      <FormField
        id="jira-projectKey"
        label="Project key"
        registration={{
          ...projectKeyRegistration,
          onChange: (event) => {
            event.target.value = event.target.value.toUpperCase();
            return projectKeyRegistration.onChange(event);
          },
        }}
        error={errors.projectKey}
        placeholder="ENG"
      />
      <FormField id="jira-issueType" label="Issue type" registration={register("issueType")} error={errors.issueType} placeholder="Task" />
      <FormField id="jira-jql" label="Issue filter (JQL)" registration={register("jql")} error={errors.jql} />
      <p className="text-xs text-muted-foreground">IO Spoke restricts this filter to the selected project and imports at most 100 issues per sync.</p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isSubmitting} loading={connection ? action.saving : action.authorizing} loadingLabel={connection ? "Saving…" : "Authorizing…"}>
          {connection ? "Save settings" : <> <ExternalLink className="mr-2 h-4 w-4" />Continue to Atlassian</>}
        </Button>
        {connection ? (
          <Button type="button" variant="outline" onClick={reconnect} disabled={isSubmitting} loading={action.authorizing} loadingLabel="Authorizing…">
            Reconnect Atlassian
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
